import React from 'react'
import { CheckCircle, Clock, AlertCircle, Activity, Loader2 } from 'lucide-react'
import { Badge } from './Badge'
import type { WorkflowProgress as WorkflowProgressType, WorkflowStep } from '../../services/profilesService'

interface WorkflowProgressProps {
  progress: WorkflowProgressType
  isActive: boolean
  error?: string
  className?: string
}

interface WorkflowStepProgressProps {
  logs: WorkflowStep[]
  currentStep?: string
  className?: string
}

// Map step names to user-friendly labels - Updated to match fixed API
const STEP_LABELS: Record<string, string> = {
  'profile_retrieval': 'Starting Workflow',
  'status_update': 'Profile Retrieved, Updating Status', 
  'ai_orchestration': 'Ready for AI Analysis',
  'pdf_enhancement': 'AI Complete, Generating PDF',
  'storage_processing': 'PDF Ready, Storing Results',
  'workflow_complete': 'All Steps Finished',
  'processing': 'Processing...'
}

const getStepLabel = (stepName: string): string => {
  if (!stepName) return 'Unknown Step'
  return STEP_LABELS[stepName] || stepName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const getProgressColor = (percentage: number, hasError: boolean = false) => {
  if (hasError) return 'bg-red-500'
  if (percentage === 100) return 'bg-green-500'
  if (percentage > 75) return 'bg-blue-500'
  if (percentage > 50) return 'bg-cyan-500'
  if (percentage > 25) return 'bg-yellow-500'
  return 'bg-gray-400'
}

const formatDuration = (ms?: number): string => {
  if (!ms || ms === 0) return ''
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60000).toFixed(1)}m`
}

export function WorkflowProgress({ progress, isActive, error, className = '' }: WorkflowProgressProps) {
  const hasError = progress.failed_steps > 0 || !!error
  const isComplete = progress.progress_percentage === 100 && !hasError
  const isProcessing = (isActive && !isComplete && !hasError) || (progress.progress_percentage > 0 && progress.progress_percentage < 100)

  return (
    <div className={`space-y-3 overflow-hidden ${className}`}>
      {/* Main Progress Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2">
            {isProcessing && (
              <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
            )}
            {isComplete && (
              <CheckCircle className="h-4 w-4 text-green-600" />
            )}
            {hasError && (
              <AlertCircle className="h-4 w-4 text-red-600" />
            )}
            <span className="font-medium">
              {hasError ? 'Processing Failed' : 
               isComplete ? 'Processing Complete' : 
               'Processing...'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-600">
              {progress.completed_steps}/{progress.total_steps} steps
            </span>
            {/* Temporarily disabled to isolate the '0' issue */}
            {false && progress.processing_time_ms && progress.processing_time_ms > 1000 && (
              <span className="text-xs text-gray-500">
                {formatDuration(progress.processing_time_ms)}
              </span>
            )}
            <Badge 
              variant={hasError ? 'destructive' : isComplete ? 'default' : 'secondary'}
              className="text-xs"
            >
              {progress.progress_percentage}%
            </Badge>
          </div>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(progress.progress_percentage, hasError)}`}
            style={{ width: `${Math.min(100, Math.max(0, progress.progress_percentage))}%` }}
          />
        </div>
      </div>

      {/* Current Step */}
      {progress.current_step && (isProcessing || progress.progress_percentage < 100) && (
        <div className="flex items-center text-sm text-gray-600">
          <Activity className="h-4 w-4 mr-2 text-blue-600" />
          <span>Current: {getStepLabel(progress.current_step)}</span>
          {/* Temporarily disabled to isolate the '0' issue */}
          {false && progress.current_step_number && progress.current_step_number > 0 && (
            <span className="ml-2 text-xs text-gray-500">
              (Step {progress.current_step_number}/{progress.total_steps})
            </span>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-start text-sm text-red-600 bg-red-50 p-2 rounded">
          <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}

export function WorkflowStepProgress({ logs, currentStep, className = '' }: WorkflowStepProgressProps) {
  if (!logs.length) return null

  // Group logs by step_number and get the latest status for each step
  const stepStatuses = logs.reduce((acc, log) => {
    const key = log.step_number
    if (!acc[key] || new Date(log.timestamp) > new Date(acc[key].timestamp)) {
      acc[key] = log
    }
    return acc
  }, {} as Record<number, WorkflowStep>)

  const sortedSteps = Object.values(stepStatuses).sort((a, b) => a.step_number - b.step_number)
  
  // Find the currently active step (started but not completed/failed)
  const activeStep = logs
    .filter(log => log.status === 'started')
    .find(log => !logs.some(laterLog => 
      laterLog.step_number === log.step_number && 
      (laterLog.status === 'completed' || laterLog.status === 'failed') &&
      new Date(laterLog.timestamp) > new Date(log.timestamp)
    ))

  return (
    <div className={`space-y-2 overflow-hidden ${className}`}>
      <h4 className="text-sm font-medium text-gray-700 mb-2">Processing Steps</h4>
      <div className="space-y-1">
        {sortedSteps.map((step) => {
          const isActive = step.step_name === currentStep || (activeStep && activeStep.step_number === step.step_number && step.status === 'started')
          const isCompleted = step.status === 'completed'
          const isFailed = step.status === 'failed'
          const isPending = step.status !== 'completed' && step.status !== 'failed' && step.status !== 'started'
          
          return (
            <div 
              key={step.step_number} 
              className={`flex items-center justify-between text-xs p-2 rounded ${
                isActive ? 'bg-blue-50' : 
                isCompleted ? 'bg-green-50' : 
                isFailed ? 'bg-red-50' : 'bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-2">
                {isCompleted && <CheckCircle className="h-3 w-3 text-green-600" />}
                {isFailed && <AlertCircle className="h-3 w-3 text-red-600" />}
                {isActive && !isCompleted && !isFailed && <Activity className="h-3 w-3 text-blue-600 animate-pulse" />}
                {isPending && <Clock className="h-3 w-3 text-gray-400" />}
                <span className={`${isActive ? 'font-medium' : ''}`}>
                  {step.step_number}. {getStepLabel(step.step_name)}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                {step.duration_ms && step.duration_ms > 0 && (
                  <span className="text-gray-500">{formatDuration(step.duration_ms)}</span>
                )}
                {step.tokens_used && step.tokens_used > 0 && (
                  <span className="text-gray-500">{step.tokens_used} tokens</span>
                )}
                {step.status && (
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    step.status === 'completed' ? 'bg-green-100 text-green-700' :
                    step.status === 'failed' ? 'bg-red-100 text-red-700' :
                    step.status === 'started' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {step.status}
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}